import { useState, useEffect, useRef } from 'react';
import { useDataProvider } from 'react-admin';
import { CustomDataProvider, NotificationCatalogItem } from '../types/dataProvider';

export type { NotificationCatalogItem };

export interface UseNotificationTypesResult {
  allTypes: NotificationCatalogItem[];
  adminSendableTypes: NotificationCatalogItem[];
  allChoices: { id: string; name: string }[];
  adminSendableChoices: { id: string; name: string }[];
  typesByCategory: Record<string, NotificationCatalogItem[]>;
  loading: boolean;
  error: string | null;
  getTypeName: (key: string) => string;
}

const FALLBACK_TYPES: NotificationCatalogItem[] = [
  { key: 'admin_announcement', category: 'marketing', admin_sendable: true },
  { key: 'marketing_campaign', category: 'marketing', admin_sendable: true },
  { key: 'system_maintenance', category: 'system', admin_sendable: true },
  { key: 'feature_update', category: 'system', admin_sendable: true },
];

const formatKey = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Module-level cache — one fetch per session
let cachedTypes: NotificationCatalogItem[] | null = null;

export function useNotificationTypes(): UseNotificationTypesResult {
  const [types, setTypes] = useState<NotificationCatalogItem[]>(cachedTypes ?? []);
  const [loading, setLoading] = useState(!cachedTypes);
  const [error, setError] = useState<string | null>(null);
  const dataProvider = useDataProvider<CustomDataProvider>();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (cachedTypes) return;

    dataProvider
      .getNotificationTypes()
      .then(({ data }) => {
        if (!mounted.current) return;
        cachedTypes = data;
        setTypes(data);
        setLoading(false);
      })
      .catch((err: any) => {
        if (!mounted.current) return;
        setError(err?.message || 'Failed to load notification types');
        setTypes(FALLBACK_TYPES);
        setLoading(false);
      });

    return () => {
      mounted.current = false;
    };
  }, [dataProvider]);

  const adminSendableTypes = types.filter((t) => t.admin_sendable);

  const typesByCategory = types.reduce<Record<string, NotificationCatalogItem[]>>((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const getTypeName = (key: string): string => {
    const found = types.find((t) => t.key === key);
    return found ? formatKey(found.key) : formatKey(key);
  };

  return {
    allTypes: types,
    adminSendableTypes,
    allChoices: types.map((t) => ({ id: t.key, name: formatKey(t.key) })),
    adminSendableChoices: adminSendableTypes.map((t) => ({ id: t.key, name: formatKey(t.key) })),
    typesByCategory,
    loading,
    error,
    getTypeName,
  };
}
